import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types";

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const go = (i: number) => setIndex((i + slides.length) % slides.length);

  return (
    <div className="relative w-full aspect-[16/9] lg:aspect-[16/8] rounded-xl overflow-hidden bg-primary-900 group">
      {slides.map((slide, i) => (
        <Link
          key={slide.id}
          to={`/news/${slide.newsId}`}
          className={`absolute inset-0 transition-opacity duration-700 cursor-pointer ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 爱爱网影视资讯`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold mb-3">
              {slide.kicker}
            </span>
            <h2 className="font-heading text-xl md:text-3xl font-bold text-white leading-snug max-w-2xl">
              {slide.title}
            </h2>
            <span className="mt-3 inline-flex items-center gap-1 text-sm text-white/85">
              阅读全文
              <i className="ri-arrow-right-line" />
            </span>
          </div>
        </Link>
      ))}

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 rounded-full bg-foreground-950/40 backdrop-blur text-white items-center justify-center hover:bg-foreground-950/60 transition-colors cursor-pointer hidden group-hover:flex"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 rounded-full bg-foreground-950/40 backdrop-blur text-white items-center justify-center hover:bg-foreground-950/60 transition-colors cursor-pointer hidden group-hover:flex"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-4 right-5 z-20 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-accent-500" : "w-1.5 bg-white/60 hover:bg-white"
            }`}
            aria-label={`第 ${i + 1} 张`}
          />
        ))}
      </div>
    </div>
  );
}